import React, { useState, useEffect } from 'react';
import { Clock, Target, TrendingUp, FileText, GitBranch, CheckSquare, AlertTriangle, Download, RefreshCw } from 'lucide-react';
import api from '../../api';
import toast from 'react-hot-toast';

interface Project {
  id: number;
  name: string;
  client_name: string;
  createdAt: string;
  expire_date: string;
}

interface HeroMetrics {
  activeWorkTime: number;
  firstPassAccuracy: number;
  avgEditsPerDoc: number;
  reworkRate: number;
  valueDiscovered: number;
  remediations: number;
}

interface DocumentProcessing {
  documentsUploaded: number;
  pagesProcessed: number;
  processingTimeMinutes: number;
  initialExtractionConfidence: number;
  confidencePostRevisions: number;
  documentsWithRevisions: number;
}

interface ProcessMapping {
  processesMapped: number;
  stepsExtracted: number;
  systemsApplicationsIdentified: number;
  processImprovementOpportunities: number;
  automationOpportunitiesIdentified: number;
  automationPercentage: number;
}

interface SOPManagement {
  sopsCreatedUpdated: number;
  totalRevisionsTracked: number;
  avgUpdateCycleDays: number;
  versionControl: string;
}

interface RiskControlIntelligence {
  risksIdentified: number;
  controlsMapped: number;
  gapsDetected: number;
  remediationsRecommended: number;
}

interface WorkTimeBreakdown {
  uploadExtract: number;
  aiReview: number;
  refinement: number;
  riskAssessment: number;
  finalReview: number;
}

interface ROIMetrics {
  project: {
    id: number;
    name: string;
    clientName: string;
    startDate: string;
    endDate: string;
    teamSize: number;
  };
  heroMetrics: HeroMetrics;
  documentProcessing: DocumentProcessing;
  processMapping: ProcessMapping;
  sopManagement: SOPManagement;
  riskControlIntelligence: RiskControlIntelligence;
  activeWorkTimeBreakdown: WorkTimeBreakdown;
  verifiedData: boolean;
}

const ROIDashboard: React.FC = () => {
  const [projects, setProjects] = useState<Project[]>([]);
  const [selectedProjectId, setSelectedProjectId] = useState<number | null>(null);
  const [metrics, setMetrics] = useState<ROIMetrics | null>(null);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);

  // Helper function to format time: show minutes if < 0.1h, otherwise hours
  const formatTime = (hours: number): string => {
    if (hours < 0.1) {
      const minutes = Math.round(hours * 60);
      return `${minutes} min`;
    }
    return `${hours}h`;
  };

  useEffect(() => {
    fetchProjects();
  }, []);

  useEffect(() => {
    if (selectedProjectId) {
      fetchMetrics(selectedProjectId);
    }
  }, [selectedProjectId]);

  const fetchProjects = async () => {
    try {
      const response = await api.get('/api/roi-metrics/projects/list');
      setProjects(response.data);
      if (response.data.length > 0) {
        setSelectedProjectId(response.data[0].id);
      }
    } catch (error: any) {
      toast.error('Failed to load projects');
    }
  };

  const fetchMetrics = async (projectId: number) => {
    setLoading(true);
    try {
      const response = await api.get(`/api/roi-metrics/project/${projectId}`);
      setMetrics(response.data);
    } catch (error: any) {
      toast.error('Failed to load metrics');
    } finally {
      setLoading(false);
    }
  };

  const handleExportToPowerPoint = async () => {
    if (!selectedProjectId || !metrics) return;

    setExporting(true);
    try {
      toast.success('PowerPoint export feature coming soon');
    } catch (error: any) {
      toast.error('Failed to export to PowerPoint');
    } finally {
      setExporting(false);
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  if (loading && !metrics) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading metrics...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 relative">
      {/* Loading Overlay */}
      {loading && metrics && (
        <div className="absolute inset-0 bg-white bg-opacity-75 flex items-center justify-center z-50 rounded-lg">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
            <p className="text-gray-600 font-medium">Loading project metrics...</p>
          </div>
        </div>
      )}

      <div className="flex justify-between items-center">
        <div className="flex-1 max-w-md">
          <label className="block text-sm font-medium text-gray-700 mb-2">Select Project</label>
          <select
            value={selectedProjectId || ''}
            onChange={(e) => setSelectedProjectId(Number(e.target.value))}
            disabled={loading}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {projects.map((project) => (
              <option key={project.id} value={project.id}>
                {project.name} {project.client_name ? `- ${project.client_name}` : ''}
              </option>
            ))}
          </select>
        </div>
        <div className="flex items-center gap-3">
          {metrics?.verifiedData && (
            <div className="flex items-center gap-2 bg-green-50 border border-green-200 rounded-lg px-4 py-2">
              <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
              <span className="text-green-700 text-sm font-medium">Real-time tracking active</span>
            </div>
          )}
          <button
            onClick={handleExportToPowerPoint}
            disabled={!metrics || exporting}
            className="flex items-center gap-2 bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700 disabled:opacity-50 disabled:cursor-not-allowed text-white px-4 py-2 rounded-lg transition-colors"
          >
            <Download size={18} />
            {exporting ? 'Exporting...' : 'Export to PowerPoint'}
          </button>
        </div>
      </div>

      {metrics && (
        <>
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <div className="flex justify-between items-start mb-6">
              <div>
                <h2 className="text-2xl font-bold text-gray-900 mb-2">{metrics.project.name}</h2>
                <div className="flex items-center gap-4 text-sm text-gray-600">
                  <span>📅 {formatDate(metrics.project.startDate)} - {formatDate(metrics.project.endDate)}</span>
                  <span>👥 Team Size: {metrics.project.teamSize}</span>
                </div>
              </div>
              {metrics.verifiedData && (
                <div className="bg-green-50 border border-green-200 rounded-lg px-4 py-2">
                  <span className="text-green-700 font-semibold">✓ VERIFIED DATA</span>
                </div>
              )}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-lg p-6 border border-blue-200">
                <div className="flex items-center gap-2 mb-3 text-blue-700">
                  <Clock size={20} />
                  <span className="text-sm font-semibold uppercase">Active Work Time</span>
                </div>
                <div className="text-4xl font-bold text-blue-900 mb-1">{metrics.heroMetrics.activeWorkTime} hrs</div>
              </div>

              <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-lg p-6 border border-green-200">
                <div className="flex items-center gap-2 mb-3 text-green-700">
                  <Target size={20} />
                  <span className="text-sm font-semibold uppercase">First-Pass Accuracy</span>
                </div>
                <div className="text-4xl font-bold text-green-900 mb-1">{metrics.heroMetrics.firstPassAccuracy}%</div>
                <div className="text-sm text-green-700">Avg {metrics.heroMetrics.avgEditsPerDoc} edits per doc</div>
                <div className="text-sm text-green-600">{metrics.heroMetrics.reworkRate}% rework rate</div>
              </div>

              <div className="bg-gradient-to-br from-purple-50 to-purple-100 rounded-lg p-6 border border-purple-200">
                <div className="flex items-center gap-2 mb-3 text-purple-700">
                  <TrendingUp size={20} />
                  <span className="text-sm font-semibold uppercase">Value Discovered</span>
                </div>
                <div className="text-4xl font-bold text-purple-900 mb-1">{metrics.heroMetrics.valueDiscovered}</div>
                <div className="text-sm text-purple-700">Control gaps identified</div>
                <div className="text-sm text-purple-600">{metrics.heroMetrics.remediations} remediations</div>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <div className="flex items-center gap-2 mb-4 text-blue-600">
                <FileText size={20} />
                <h3 className="text-lg font-semibold text-gray-900">Document Processing</h3>
              </div>
              <div className="space-y-3">
                <div className="flex justify-between items-center py-2 border-b border-gray-100">
                  <span className="text-gray-700">Documents uploaded</span>
                  <span className="font-semibold text-gray-900">{metrics.documentProcessing.documentsUploaded}</span>
                </div>
                <div className="flex justify-between items-center py-2 border-b border-gray-100">
                  <span className="text-gray-700">Pages processed</span>
                  <span className="font-semibold text-gray-900">{metrics.documentProcessing.pagesProcessed.toLocaleString()}</span>
                </div>
                <div className="flex justify-between items-center py-2 border-b border-gray-100">
                  <span className="text-gray-700">Processing time</span>
                  <span className="font-semibold text-green-600">{metrics.documentProcessing.processingTimeMinutes} min ✓</span>
                </div>
                <div className="flex justify-between items-center py-2 border-b border-gray-100">
                  <div className="flex flex-col">
                    <span className="text-gray-700">Extraction confidence</span>
                    <span className="text-xs text-blue-600 mt-1">Initial (based on Perplexity rating)</span>
                  </div>
                  <span className="font-semibold text-gray-900">{metrics.documentProcessing.initialExtractionConfidence}%</span>
                </div>
                <div className="flex justify-between items-center py-2">
                  <span className="text-gray-700">Confidence post-revisions</span>
                  <span className="font-semibold text-blue-600">{metrics.documentProcessing.confidencePostRevisions}%</span>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <div className="flex items-center gap-2 mb-4 text-purple-600">
                <GitBranch size={20} />
                <h3 className="text-lg font-semibold text-gray-900">Process Mapping</h3>
              </div>
              <div className="space-y-3">
                <div className="flex justify-between items-center py-2 border-b border-gray-100">
                  <span className="text-gray-700">Processes mapped</span>
                  <span className="font-semibold text-gray-900">{metrics.processMapping.processesMapped}</span>
                </div>
                <div className="flex justify-between items-center py-2 border-b border-gray-100">
                  <span className="text-gray-700">Steps extracted</span>
                  <span className="font-semibold text-gray-900">{metrics.processMapping.stepsExtracted}</span>
                </div>
                <div className="flex justify-between items-center py-2 border-b border-gray-100">
                  <span className="text-gray-700">Systems/Applications identified</span>
                  <span className="font-semibold text-gray-900">{metrics.processMapping.systemsApplicationsIdentified}</span>
                </div>
                <div className="flex justify-between items-center py-2 border-b border-gray-100">
                  <span className="text-gray-700">Process Improvement Opportunities</span>
                  <span className="font-semibold text-blue-600">{metrics.processMapping.processImprovementOpportunities}</span>
                </div>
                <div className="flex justify-between items-center py-2 border-b border-gray-100">
                  <span className="text-gray-700">Automation Opportunities</span>
                  <span className="font-semibold text-gray-900">{metrics.processMapping.automationOpportunitiesIdentified}</span>
                </div>
                <div className="flex justify-between items-center py-2">
                  <span className="text-gray-700">Automation Percentage</span>
                  <span className="font-semibold text-green-600">{metrics.processMapping.automationPercentage}%</span>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <div className="flex items-center gap-2 mb-4 text-green-600">
                <CheckSquare size={20} />
                <h3 className="text-lg font-semibold text-gray-900">SOP Management</h3>
              </div>
              <div className="space-y-3">
                <div className="flex justify-between items-center py-2 border-b border-gray-100">
                  <span className="text-gray-700">SOPs created/updated</span>
                  <span className="font-semibold text-gray-900">{metrics.sopManagement.sopsCreatedUpdated}</span>
                </div>
                <div className="flex justify-between items-center py-2 border-b border-gray-100">
                  <span className="text-gray-700">Total revisions tracked</span>
                  <span className="font-semibold text-gray-900">{metrics.sopManagement.totalRevisionsTracked}</span>
                </div>
                <div className="flex justify-between items-center py-2 border-b border-gray-100">
                  <span className="text-gray-700">Avg update cycle</span>
                  <span className="font-semibold text-green-600">{metrics.sopManagement.avgUpdateCycleDays} days ✓</span>
                </div>
                <div className="flex justify-between items-center py-2">
                  <span className="text-gray-700">Version control</span>
                  <span className="font-semibold text-gray-900">{metrics.sopManagement.versionControl}</span>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <div className="flex items-center gap-2 mb-4 text-orange-600">
                <AlertTriangle size={20} />
                <h3 className="text-lg font-semibold text-gray-900">Risk & Control Intelligence</h3>
              </div>
              <div className="space-y-3">
                <div className="flex justify-between items-center py-2 border-b border-gray-100">
                  <span className="text-gray-700">Risks identified</span>
                  <span className="font-semibold text-gray-900">{metrics.riskControlIntelligence.risksIdentified}</span>
                </div>
                <div className="flex justify-between items-center py-2 border-b border-gray-100">
                  <span className="text-gray-700">Controls mapped</span>
                  <span className="font-semibold text-gray-900">{metrics.riskControlIntelligence.controlsMapped}</span>
                </div>
                <div className="flex justify-between items-center py-2 border-b border-gray-100">
                  <span className="text-gray-700">Gaps detected</span>
                  <span className="font-semibold text-gray-900">{metrics.riskControlIntelligence.gapsDetected}</span>
                </div>
                <div className="flex justify-between items-center py-2">
                  <span className="text-gray-700">Remediations recommended</span>
                  <span className="font-semibold text-gray-900">{metrics.riskControlIntelligence.remediationsRecommended}</span>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <div className="flex items-center gap-2 mb-6 text-blue-600">
              <Clock size={20} />
              <h3 className="text-lg font-semibold text-gray-900">Active Work Time Breakdown</h3>
            </div>
            <div className="grid grid-cols-5 gap-4 mb-6">
              <div className="text-center">
                <div className="text-gray-600 text-sm mb-2">Upload & Extract</div>
                <div className="text-3xl font-bold text-blue-600">{formatTime(metrics.activeWorkTimeBreakdown.uploadExtract)}</div>
              </div>
              <div className="text-center">
                <div className="text-gray-600 text-sm mb-2">AI Review</div>
                <div className="text-3xl font-bold text-purple-600">{formatTime(metrics.activeWorkTimeBreakdown.aiReview)}</div>
              </div>
              <div className="text-center">
                <div className="text-gray-600 text-sm mb-2">Refinement</div>
                <div className="text-3xl font-bold text-green-600">{formatTime(metrics.activeWorkTimeBreakdown.refinement)}</div>
              </div>
              <div className="text-center">
                <div className="text-gray-600 text-sm mb-2">Risk Assessment</div>
                <div className="text-3xl font-bold text-orange-600">{formatTime(metrics.activeWorkTimeBreakdown.riskAssessment)}</div>
              </div>
              <div className="text-center">
                <div className="text-gray-600 text-sm mb-2">Final Review</div>
                <div className="text-3xl font-bold text-indigo-600">{formatTime(metrics.activeWorkTimeBreakdown.finalReview)}</div>
              </div>
            </div>
            <div className="border-t border-gray-200 pt-4">
              <div className="flex justify-between items-center">
                <span className="text-gray-700 font-medium">Total Active Work Time</span>
                <span className="text-3xl font-bold text-gray-900">{metrics.heroMetrics.activeWorkTime} hours</span>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default ROIDashboard;
